import { useState, useRef, Fragment } from 'react'
import type { TouchEvent } from 'react'
import { X, Search, ArrowLeft, Check } from 'lucide-react'
import { useMenuItems, useMenuCategories, groupMenuItemsByCategory } from '@/hooks/useMenu'
import { useCateringPricing } from '@/hooks/useCatering'
import { cn } from '@/lib/utils'
import type { TraySize } from '@/types/app'

export interface MatrixAddPayload {
  menuItemId: string
  name: string
  traySize: Exclude<TraySize, 'custom'>
  price: number
  serves: number | null
  // Per-lb sizes only — starting quantity is this item's minimum order
  // (lbs), not a flat 1, same as adding one via search.
  min?: number | null
}

// Minimal shape of an order row — used only to figure out what's already on
// the order so matching cells can be shown green.
export interface MatrixOrderItem {
  menu_item_id: string | null
  tray_size: TraySize | null
  quantity: number
}

interface Props {
  open: boolean
  onClose: () => void
  onAdd: (payload: MatrixAddPayload) => void
  onRemove: (menuItemId: string, traySize: Exclude<TraySize, 'custom'>) => void
  orderItems: MatrixOrderItem[]
}

const SIZE_COLS: { size: Exclude<TraySize, 'custom'>; label: string }[] = [
  { size: 'per_piece',    label: 'Pc'  },
  { size: 'half_tray',    label: 'Sm'  },
  { size: 'medium_tray',  label: 'Med' },
  { size: 'full_tray',    label: 'Lg'  },
  { size: 'per_lb',       label: 'Lb'  },
  { size: 'boxed_per_lb', label: 'Box' },
]

const SWIPE_CLOSE_THRESHOLD = 90

// Full-screen slide-in matrix of the catering menu — tap a price cell to add
// that item/size straight onto the order. Swipe right (or back arrow) returns
// to the inquiry form.
export function CateringMenuMatrixSheet({ open, onClose, onAdd, onRemove, orderItems }: Props) {
  const { data: allItems = [] }   = useMenuItems()
  const { data: categories = [] } = useMenuCategories()
  const { data: pricingMap }      = useCateringPricing()

  const [search, setSearch]         = useState('')
  const [dragX, setDragX]           = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const startX = useRef(0)

  // menuItemId:traySize → quantity currently on the order, so cells can
  // reflect real order state (and be toggled off again) rather than just
  // flashing on tap.
  const inOrder = new Map<string, number>()
  for (const oi of orderItems) {
    if (!oi.menu_item_id || !oi.tray_size) continue
    const key = `${oi.menu_item_id}:${oi.tray_size}`
    inOrder.set(key, (inOrder.get(key) ?? 0) + oi.quantity)
  }

  const cateringItems = allItems.filter(i => i.available_catering)
  const q = search.trim().toLowerCase()
  const filtered = q
    ? cateringItems.filter(i => i.name.toLowerCase().includes(q) || (i.regional_name ?? '').toLowerCase().includes(q))
    : cateringItems

  const grouped = groupMenuItemsByCategory(filtered, categories)

  function priceFor(menuItemId: string, size: Exclude<TraySize, 'custom'>) {
    const p = pricingMap?.get(menuItemId)
    if (!p) return { price: null as number | null, serves: null as number | null, min: null as number | null }
    switch (size) {
      case 'half_tray':    return { price: p.half_tray_price,    serves: p.half_tray_serves, min: null }
      case 'medium_tray':  return { price: p.medium_tray_price,  serves: p.medium_tray_serves, min: null }
      case 'full_tray':    return { price: p.full_tray_price,    serves: p.full_tray_serves, min: null }
      case 'per_piece':    return { price: p.per_piece_price,    serves: null, min: null }
      // Tap adds this item at its minimum order (lbs) — the customer/staff
      // refines the exact quantity afterward in the order list, same as
      // tapping a tray adds one and quantity gets adjusted from there.
      case 'per_lb':       return { price: p.per_lb_price,       serves: null, min: p.per_lb_min }
      case 'boxed_per_lb': return { price: p.boxed_per_lb_price, serves: null, min: p.boxed_per_lb_min }
    }
  }

  // Tap toggles: add if not on the order yet, remove if it's already there —
  // so the sheet doubles as a place to look at and change the current order.
  function handleTap(item: { id: string; name: string }, size: Exclude<TraySize, 'custom'>) {
    const key = `${item.id}:${size}`
    if (inOrder.has(key)) {
      onRemove(item.id, size)
      return
    }
    const { price, serves, min } = priceFor(item.id, size)
    if (price == null) return
    onAdd({ menuItemId: item.id, name: item.name, traySize: size, price, serves, min })
  }

  // Edge-swipe-back: dragging the sheet to the right past the threshold closes it.
  function onTouchStart(e: TouchEvent) {
    startX.current = e.touches[0].clientX
    setIsDragging(true)
  }
  function onTouchMove(e: TouchEvent) {
    if (!isDragging) return
    const dx = e.touches[0].clientX - startX.current
    if (dx > 0) setDragX(dx)
  }
  function onTouchEnd() {
    if (dragX > SWIPE_CLOSE_THRESHOLD) onClose()
    setDragX(0)
    setIsDragging(false)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div
        className="relative w-full sm:max-w-md bg-white h-full flex flex-col shadow-2xl"
        style={{
          transform: `translateX(${dragX}px)`,
          transition: isDragging ? 'none' : 'transform 0.2s ease-out',
          animation: dragX === 0 ? 'catering-matrix-in 0.2s ease-out' : undefined,
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-200 shrink-0">
          <button onClick={onClose} className="p-1.5 -ml-1.5 rounded hover:bg-stone-100 text-stone-500" title="Back to inquiry">
            <ArrowLeft size={18} />
          </button>
          <h3 className="font-semibold text-stone-900 text-sm">Catering Menu</h3>
          <button onClick={onClose} className="ml-auto p-1.5 rounded hover:bg-stone-100 text-stone-400">
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-2.5 border-b border-stone-200 shrink-0">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              type="search"
              autoFocus
              placeholder="Search item…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full border border-stone-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </div>
        </div>

        {/* Matrix: Category (grouped row) × Item × Size */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          <table className="w-full text-xs border-collapse table-fixed">
            <colgroup>
              <col />
              {SIZE_COLS.map(c => <col key={c.size} className="w-11" />)}
            </colgroup>
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b border-stone-200">
                <th className="px-3 py-2 text-left font-semibold text-stone-500">Item</th>
                {SIZE_COLS.map(c => (
                  <th key={c.size} className="px-0.5 py-2 text-center font-semibold text-stone-500">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grouped.length === 0 && (
                <tr>
                  <td colSpan={SIZE_COLS.length + 1} className="px-4 py-8 text-center text-stone-400">
                    No items match &quot;{search}&quot;
                  </td>
                </tr>
              )}
              {grouped.map(({ cat, items }) => (
                <Fragment key={cat.id}>
                  <tr className="bg-stone-50">
                    <td colSpan={SIZE_COLS.length + 1} className="px-3 py-1 text-2xs font-bold uppercase tracking-widest text-stone-400 border-y border-stone-200">
                      {cat.name}
                    </td>
                  </tr>
                  {items.map(item => (
                    <tr key={item.id} className="border-b border-stone-100">
                      <td className="px-3 py-1.5 font-medium text-stone-800 truncate">{item.name}</td>
                      {SIZE_COLS.map(c => {
                        const { price } = priceFor(item.id, c.size)
                        const key = `${item.id}:${c.size}`
                        const qty = inOrder.get(key)
                        if (price == null) {
                          return <td key={c.size} className="px-0.5 py-1.5 text-center text-stone-200">—</td>
                        }
                        return (
                          <td key={c.size} className="px-0.5 py-1">
                            <button
                              type="button"
                              onClick={() => handleTap(item, c.size)}
                              title={qty ? 'On the order — tap to remove' : `$${price.toFixed(2)} — tap to add`}
                              className={cn(
                                'w-full rounded px-1 py-1.5 text-2xs font-semibold transition-colors',
                                qty
                                  ? 'bg-success text-white hover:bg-success-dark'
                                  : 'bg-brand-50 text-brand-700 hover:bg-brand-100 active:bg-brand-200'
                              )}
                            >
                              {qty
                                ? <span className="flex items-center justify-center gap-0.5"><Check size={11} />{qty > 1 ? `×${qty}` : ''}</span>
                                : `$${price.toFixed(0)}`}
                            </button>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-2.5 border-t border-stone-200 shrink-0 text-center">
          <span className="text-2xs text-stone-400">Tap to add · green = on the order, tap to remove · swipe right or ← to go back</span>
        </div>
      </div>

      <style>{`
        @keyframes catering-matrix-in {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </div>
  )
}
